import { useEffect, useRef } from 'react';
import { getTimeDomain } from '../analyserDsp';
import { gainToDb, meterFromDb, rmsDbfs } from '../../../lib/audioMath';

export function useMetersEnvelope(
  status: string,
  analyzersRef: React.MutableRefObject<Record<string, AnalyserNode>>,
  getEffectiveGain: (role: string) => number,
  masterEnvelopeEnabled: boolean,
  masterAnalyserRef: React.MutableRefObject<AnalyserNode | null>,
) {
  const envFps = 30;
  const envSize = 4096;
  const masterEnvRef = useRef<Float32Array>(new Float32Array(envSize));
  const masterEnvHeadRef = useRef(0);
  const levelsRef = useRef<Record<string, { preMeter: number; srcDb: number; postMeter: number; outDb?: number; attenuationDb: number }>>({});

  useEffect(() => {
    let raf: number | null = null;
    let last = 0;
    const tick = (ts?: number) => {
      const next: typeof levelsRef.current = {};
      for (const [role, an] of Object.entries(analyzersRef.current)) {
        try {
          const buf = getTimeDomain(an);
          const srcDb = rmsDbfs(buf);
          const preMeter = meterFromDb(srcDb);
          const eff = getEffectiveGain(role);
          const attDb = gainToDb(eff);
          const outDb = eff === 0 ? srcDb : (isFinite(attDb) ? srcDb + attDb : undefined);
          const postMeter = typeof outDb === 'number' ? meterFromDb(outDb) : 0;
          next[role] = { preMeter, srcDb, postMeter, outDb, attenuationDb: attDb };
        } catch {}
      }
      levelsRef.current = next;
      if (masterEnvelopeEnabled) {
        // Use the master analyser node tapped post-EQ for envelope, if available
        const man = masterAnalyserRef.current;
        if (man) {
          try {
            const tdbuf = getTimeDomain(man);
            const db = rmsDbfs(tdbuf);
            const meter = meterFromDb(db);
            const head = masterEnvHeadRef.current;
            masterEnvRef.current[head] = Math.max(0, Math.min(1, meter));
            masterEnvHeadRef.current = (head + 1) % envSize;
          } catch {}
        } else {
          // Fallback: derive from per-stem meters if master node missing
          let maxMeter = 0;
          for (const v of Object.values(next)) maxMeter = Math.max(maxMeter, v.postMeter);
          const head = masterEnvHeadRef.current;
          masterEnvRef.current[head] = Math.max(0, Math.min(1, maxMeter));
          masterEnvHeadRef.current = (head + 1) % envSize;
        }
      }
      // Throttle ~30 FPS to reduce state churn
      const now = typeof ts === 'number' ? ts : performance.now();
      if (now - last >= 33) { last = now; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [status, analyzersRef, getEffectiveGain, masterEnvelopeEnabled, masterAnalyserRef]);

  return {
    getLevels: () => levelsRef.current,
    getMasterEnvelope: () => masterEnvelopeEnabled ? { buffer: masterEnvRef.current, head: masterEnvHeadRef.current, fps: envFps } : null,
    reset: () => {
      try { levelsRef.current = {}; } catch {}
      try { masterEnvRef.current.fill(0); masterEnvHeadRef.current = 0; } catch {}
    }
  } as const;
}
